import React, { useCallback, useEffect, useState } from 'react';
import { UserPlus, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { workflowAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';

const WorkflowDelegations = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [delegateId, setDelegateId] = useState('');
  const [templateKey, setTemplateKey] = useState('');
  const [reason, setReason] = useState('');
  const [endsAt, setEndsAt] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await workflowAPI.listDelegations();
      setRows(res?.data?.delegations || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load delegations'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!delegateId) return toast.error('Enter the delegate user ID');
    try {
      setSaving(true);
      await workflowAPI.createDelegation({
        delegate_user_id: Number(delegateId),
        template_key: templateKey.trim() || null,
        reason: reason.trim() || null,
        ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      });
      toast.success('Delegation created');
      setDelegateId(''); setTemplateKey(''); setReason(''); setEndsAt('');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not create delegation'));
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (id) => {
    try {
      await workflowAPI.revokeDelegation(id);
      toast.success('Delegation revoked');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not revoke'));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <CardTitle className="text-foreground text-base">Delegate my approvals</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            While active, the delegate can act on any workflow step you could approve, recorded as a proxy in the audit trail.
          </p>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-3 sm:grid-cols-4 items-end">
            <div className="space-y-1">
              <Label>Delegate user ID</Label>
              <Input value={delegateId} onChange={(e) => setDelegateId(e.target.value)} placeholder="e.g. 42" />
            </div>
            <div className="space-y-1">
              <Label>Workflow (optional)</Label>
              <Input value={templateKey} onChange={(e) => setTemplateKey(e.target.value)} placeholder="all workflows" />
            </div>
            <div className="space-y-1">
              <Label>Ends at (optional)</Label>
              <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </div>
            <Button onClick={create} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-1" /> Delegate</>}
            </Button>
          </div>
          <div className="mt-3 space-y-1">
            <Label>Reason (optional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="On leave 12–18 Jul" />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <CardTitle className="text-foreground text-base">Active delegations</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No active delegations.</p>
          ) : (
            <div className="space-y-2">
              {rows.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-3">
                  <div className="text-sm">
                    <p className="text-foreground">
                      User #{d.delegator_user_id} → <span className="font-medium">User #{d.delegate_user_id}</span>
                      {d.template_key ? <span className="text-muted-foreground"> · {d.template_key}</span> : <span className="text-muted-foreground"> · all workflows</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {d.reason || 'No reason given'}{d.ends_at ? ` · until ${new Date(d.ends_at).toLocaleString()}` : ''}
                    </p>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => revoke(d.id)} title="Revoke"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkflowDelegations;
